import {
  ActionIcon,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  NumberInput,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconArrowBackUp,
  IconBox,
  IconCoin,
  IconDeviceFloppy,
  IconFileText,
  IconLanguage,
  IconPlus,
  IconSend,
  IconSparkles,
  IconTag,
} from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { createProduct, pushProductPrice, updateProduct } from "@/api/products";
import { improveDescription } from "@/api/improve";
import { translate } from "@/api/translate";
import { notify } from "@/lib/notify";
import { pushSummary } from "@/lib/pushResult";
import { useAccess } from "@/hooks/useAccess";
import { buildTranslations, flattenTranslations } from "@/lib/menuLang";
import { useMenuLanguages } from "@/hooks/useMenuLanguages";
import { TranslationFields } from "@/components/TranslationFields";
import { FormModalFooter } from "@/components/FormModalFooter";
import { FormSection } from "@/components/FormSection";

const stripHtml = (html) =>
  new DOMParser()
    .parseFromString(html || "", "text/html")
    .body.textContent?.trim() ?? "";

// name + description alanlarını dil kodu son ekiyle (nameEN, descriptionAR...) tutar.
const TRANSLATED_FIELDS = ["name", "description"];

// Taban dil örnekleri. Çeviri alanlarının ipucu artık dile özel değil, KAYNAK metnin kendisi
// (bkz. components/TranslationFields) — dil listesi veriden geldiği için dile özel örnek tutulamaz.
const NAME_PLACEHOLDER = "Örn. Adana Kebap";
const DESC_PLACEHOLDER = "Örn. Taze malzemelerle hazırlanan özel lezzet";

export function ProductFormModal({ opened, onClose, product, categories }) {
  const { can, allBranches } = useAccess();
  const canEditCatalog = can("products");
  const queryClient = useQueryClient();
  const isEdit = Boolean(product);
  const { contentLanguages, base, baseCode } = useMenuLanguages();
  // Taban dil de şeritte: alanı ayrı dursaydı aynı iş iki kez yer kaplıyordu. Sırada ilk, çünkü
  // içeriğin kaynağı o ve zorunlu alan orada.
  const languages = base ? [base, ...contentLanguages] : contentLanguages;
  // Ad ve açıklama şeritleri aynı dili gösteriyor; durum burada, ikisi de buradan besleniyor.
  const [translationLang, setTranslationLang] = useState(null);
  // İyileştirme yalnızca taban dilde anlamlı — diller yüklenmeden önce (translationLang null)
  // de kapalı kalıyor, çünkü hangi sekmede olunduğu henüz belli değil.
  const onBaseLanguage = Boolean(baseCode) && translationLang === baseCode;

  const form = useForm({
    initialValues: {
      name: "",
      categoryId: "",
      basePrice: 0,
      integrationPrice: "",
      oldPrice: "",
      isNew: false,
      description: "",
      // Çeviri alanları BURADA sayılamaz: hangi diller açık olduğu veriden geliyor ve bu
      // obje bir kez değerlendiriliyor. Alanlar aşağıdaki effect'te kuruluyor.
    },
    validate: {
      name: (value) =>
        value.trim().length >= 3 ? null : "TR adı en az 3 karakter",
      categoryId: (value) => (value ? null : "Kategori seçin"),
      basePrice: (value) => (value || value === 0 ? null : "Fiyat gerekli"),
    },
  });

  useEffect(() => {
    if (!opened) {
      return;
    }
    // Geri al, açık olduğu formun metnini tutuyor — modal başka bir ürünle açıldığında
    // bırakılırsa yanlış ürünün açıklamasını geri yazardı.
    setDescriptionBeforeImprove(null);
    // Yeni kayıtta da setValues: form.reset() initialValues'a dönüyor ve orada çeviri alanları
    // yok, yani Mantine o alanları kontrolsüz input sayardı. Dil listesi sonradan geldiği için
    // bağımlılıkta da duruyor.
    form.setValues({
      name: product?.name ?? "",
      categoryId: String(product?.categoryId ?? ""),
      basePrice: product?.basePrice ?? 0,
      integrationPrice: product?.integrationPrice ?? "",
      oldPrice: product?.oldPrice ?? "",
      isNew: product?.isNew ?? false,
      description: product?.description ?? "",
      ...flattenTranslations(
        product?.translations,
        TRANSLATED_FIELDS,
        contentLanguages,
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, product, contentLanguages]);

  const categoryOptions = useMemo(
    () =>
      categories.map((item) => ({ value: String(item.id), label: item.name })),
    [categories],
  );

  // Çeviri/iyileştirme çağrılarına bağlam olarak gidiyor. Formdaki değer id, uçlar ise adı
  // istiyor — pazaryeri modeli "Tatlılar" ile "İçecekler" arasında farklı yazıyor.
  const categoryName = categories.find(
    (item) => String(item.id) === form.values.categoryId,
  )?.name;

  // Reactive validity mirroring the `validate` rules — Mantine's form.isValid() left the button
  // stuck disabled, so derive it from the current values instead.
  const canSubmit =
    form.values.name.trim().length >= 3 &&
    Boolean(form.values.categoryId) &&
    (Boolean(form.values.basePrice) || form.values.basePrice === 0);

  const buildPayload = (values) => ({
    name: values.name.trim(),
    categoryId: Number(values.categoryId),
    basePrice: values.basePrice,
    integrationPrice:
      values.integrationPrice === "" || values.integrationPrice == null
        ? null
        : Number(values.integrationPrice),
    oldPrice:
      values.oldPrice === "" || values.oldPrice == null
        ? null
        : Number(values.oldPrice),
    isNew: values.isNew,
    description: values.description || null,
    translations: buildTranslations(
      values,
      TRANSLATED_FIELDS,
      contentLanguages,
    ),
  });

  const mutation = useMutation({
    mutationFn: (values) =>
      isEdit
        ? updateProduct(product.id, buildPayload(values))
        : createProduct(buildPayload(values)),
    onSuccess: () => {
      notify.success(isEdit ? "Ürün güncellendi" : "Ürün oluşturuldu");
      queryClient.invalidateQueries({ queryKey: ["products"] });
      onClose();
    },
    onError: () => notify.error("İşlem başarısız"),
  });

  // Ayrı "Entegrasyona Gönder": önce ürünü kaydeder, sonra tüm şubelerdeki kanallara iter.
  // POS'a taban/şube fiyatı gider, pazaryerine ent fiyat. İlgili fiyat olmayan kanala gitmez.
  const posMutation = useMutation({
    mutationFn: async (values) => {
      await updateProduct(product.id, buildPayload(values));
      return pushProductPrice(product.id);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      const failed = result?.failed ?? [];

      if (failed.length) {
        // Kanal reddetti (ör. Trendyol recurring-price) — genel mesaj, ham hatayı basma.
        notify.error(
          result?.pushed
            ? `${pushSummary(result.pushed, result)} · ${failed.length} entegrasyon reddetti`
            : "Gönderilemedi",
        );
      } else if (result?.pushed) {
        notify.success(pushSummary(result.pushed, result));
      } else if (!result?.connections) {
        notify.info("Aktif bağlantı yok");
      } else if (result?.noPrice) {
        notify.info("Entegrasyon için fiyat tanımlı değil — gönderilmedi");
      } else {
        notify.info("Ürün eşleşmemiş ya da fiyat aynı — gönderilmedi");
      }
    },
    onError: () => notify.error("Entegrasyon gönderimi başarısız"),
  });

  // Gönderim POS röle üzerinden 10 sn'yi bulabiliyor. O sırada pencere kapanabilirse kullanıcı
  // isteği yarıda bıraktığını sanıyor (istek sürüyor) ve sonucu hiç görmüyor.
  const pushing = posMutation.isPending;
  const closeIfIdle = () => {
    if (!pushing) {
      onClose();
    }
  };

  // Bir TR alanını (name / description) tek seferde tüm dillere çevirir. isHtml → rich text (etiketi
  // temizleyip çevir, sonucu <p> ile sar).
  const [translatingField, setTranslatingField] = useState(null);
  const translateField = async (field, isHtml) => {
    const src = isHtml
      ? stripHtml(form.values[field])
      : form.values[field].trim();
    if (!src) {
      return;
    }
    setTranslatingField(field);
    try {
      const results = await Promise.all(
        contentLanguages.map((lang) =>
          translate(src, lang.code, {
            field,
            name: form.values.name,
            category: categoryName,
          }),
        ),
      );
      contentLanguages.forEach((lang, index) => {
        if (results[index]) {
          form.setFieldValue(
            `${field}${lang.code.toUpperCase()}`,
            isHtml ? `<p>${results[index]}</p>` : results[index],
          );
        }
      });
      notify.success("Diğer dillere çevrildi");
    } catch {
      notify.error("Çeviri başarısız");
    } finally {
      setTranslatingField(null);
    }
  };

  const renderTranslate = (field, enabled, isHtml = false) => (
    <Button
      variant="light"
      size="xs"
      color="chocolate"
      leftSection={<IconLanguage size={16} />}
      loading={translatingField === field}
      onClick={() => translateField(field, isHtml)}
      disabled={!enabled || contentLanguages.length === 0}
      title={
        contentLanguages.length === 0
          ? "Menüde tek dil açık, çevrilecek dil yok"
          : undefined
      }
    >
      Diğer Dillere Çevir
    </Button>
  );

  // Kabaca yazılmış TR açıklamayı AI ile yeniden yazar. Alanın üzerine yazıyor, o yüzden önceki
  // metin geri alınabilsin diye saklanıyor — kullanıcının kendi cümlesini geri getirmenin başka
  // yolu yok (form geçmişi tutmuyor).
  const [improving, setImproving] = useState(false);
  const [descriptionBeforeImprove, setDescriptionBeforeImprove] =
    useState(null);
  const improveDescriptionField = async () => {
    const src = stripHtml(form.values.description);
    if (!src) {
      return;
    }
    setImproving(true);
    try {
      const improved = await improveDescription(
        src,
        form.values.name,
        categoryName,
      );
      if (!improved) {
        notify.error("Açıklama iyileştirilemedi");
        return;
      }
      setDescriptionBeforeImprove(form.values.description);
      form.setFieldValue("description", `<p>${improved}</p>`);
      notify.success("Açıklama iyileştirildi — çeviriyi bundan sonra çalıştır");
    } catch {
      notify.error("Açıklama iyileştirilemedi");
    } finally {
      setImproving(false);
    }
  };

  const undoImprove = () => {
    form.setFieldValue("description", descriptionBeforeImprove);
    setDescriptionBeforeImprove(null);
  };

  const currencyProps = {
    min: 0,
    hideControls: true,
    thousandSeparator: ".",
    decimalSeparator: ",",
    leftSection: (
      <Text size="sm" c="dimmed">
        ₺
      </Text>
    ),
  };

  return (
    <Modal
      opened={opened}
      onClose={closeIfIdle}
      closeOnClickOutside={!pushing}
      closeOnEscape={!pushing}
      closeButtonProps={{
        disabled: pushing,
        title: pushing ? "Gönderim sürüyor" : undefined,
      }}
      centered
      radius="lg"
      size="lg"
      padding="lg"
      scrollAreaComponent={ScrollArea.Autosize}
      title={
        <Group gap="sm">
          <ThemeIcon variant="light" color="chocolate" radius="md" size="lg">
            <IconBox size={20} />
          </ThemeIcon>
          <div>
            <Text fw={600}>{isEdit ? "Ürün Düzenle" : "Yeni Ürün"}</Text>
            <Text size="xs" c="dimmed">
              {isEdit ? "Ürün bilgilerini güncelle" : "Yeni bir ürün oluştur"}
            </Text>
          </div>
        </Group>
      }
    >
      <form
        onSubmit={form.onSubmit((values) => mutation.mutate(values))}
        style={{ position: "relative" }}
      >
        <LoadingOverlay
          visible={pushing}
          zIndex={5}
          overlayProps={{ blur: 1, backgroundOpacity: 0.35 }}
          loaderProps={{ size: "sm" }}
        />
        <Stack gap="md">
          <FormSection
            icon={IconTag}
            title="Temel Bilgiler"
            description="Ad, kategori ve rozet"
            action={renderTranslate("name", Boolean(form.values.name.trim()))}
          >
            <Stack gap="sm">
              {/* Taban dil ve çeviriler tek şeritte. Seçili dil açıklama bölümüyle
                                ORTAK: iki şerit ayrı durum tutsaydı kullanıcı burada İngilizce,
                                aşağıda Arapça'da kalabilirdi. */}
              <TranslationFields
                languages={languages}
                baseCode={baseCode}
                form={form}
                fields={[
                  {
                    name: "name",
                    label: "Ürün Adı",
                    placeholder: NAME_PLACEHOLDER,
                  },
                ]}
                active={translationLang}
                onActiveChange={setTranslationLang}
              />
              <Select
                withCheckIcon={false}
                label="Kategori"
                searchable
                data={categoryOptions}
                placeholder="Kategori seç"
                {...form.getInputProps("categoryId")}
              />
              <Group
                justify="space-between"
                wrap="nowrap"
                px="sm"
                py={8}
                style={{
                  border: "1px solid var(--mantine-color-default-border)",
                  borderRadius: "var(--mantine-radius-md)",
                }}
              >
                <div>
                  <Text size="sm" fw={500}>
                    YENİ rozeti
                  </Text>
                  <Text size="xs" c="dimmed">
                    Menüde ürünün üstünde &quot;YENİ&quot; etiketi gösterir
                  </Text>
                </div>
                <Switch
                  color="teal"
                  size="md"
                  checked={form.values.isNew}
                  onChange={(event) =>
                    form.setFieldValue("isNew", event.currentTarget.checked)
                  }
                />
              </Group>
            </Stack>
          </FormSection>

          {/* Buradaki üç alan da ürün-seviyesi, yani TÜM şubeleri etkiler. Şube kapsamlı
                        kullanıcı fiyatı kendi şubesinin override'ından değiştirir (Şubeler →
                        Menüyü Yönet), o yüzden bu bölüm ona hiç gösterilmez. */}
          {canEditCatalog && (
            <FormSection
              icon={IconCoin}
              title="Fiyatlandırma"
              description="Taban menü fiyatı ve entegrasyon (pazaryeri/POS) fiyatı"
            >
              <Stack gap="sm">
                <SimpleGrid
                  cols={{ base: 1, sm: 2 }}
                  spacing="md"
                  verticalSpacing="xs"
                >
                  <NumberInput
                    label="Taban Fiyat"
                    description="Menüde görünen güncel satış fiyatı"
                    {...currencyProps}
                    {...form.getInputProps("basePrice")}
                  />
                  <NumberInput
                    label="Eski Fiyat"
                    description="Taban'dan yüksek olmalı — üstü çizili çıkar"
                    {...currencyProps}
                    {...form.getInputProps("oldPrice")}
                  />
                </SimpleGrid>
                <NumberInput
                  label="Ent. Taban Fiyat"
                  description="Pazaryerine gönderilen fiyat (POS taban alır)"
                  {...currencyProps}
                  {...form.getInputProps("integrationPrice")}
                />
                {/* Bu buton TÜM şubelerin kanallarına gönderir ve şube override'larını
                                temizler — tenant geneli. API'de requireFullAccess korur. */}
                {isEdit && allBranches && (
                  <Group justify="center">
                    <Button
                      size="xs"
                      variant="light"
                      color="teal"
                      leftSection={<IconSend size={14} />}
                      loading={posMutation.isPending}
                      disabled={!canSubmit || mutation.isPending}
                      onClick={() => posMutation.mutate(form.values)}
                    >
                      Entegrasyona Gönder
                    </Button>
                  </Group>
                )}
              </Stack>
            </FormSection>
          )}

          <FormSection
            icon={IconFileText}
            title="Açıklamalar"
            description="Türkçe baz; diğer diller menüde o dil seçilince gösterilir"
            action={renderTranslate(
              "description",
              Boolean(stripHtml(form.values.description)),
              true,
            )}
          >
            <Stack gap="sm">
              <TranslationFields
                languages={languages}
                baseCode={baseCode}
                form={form}
                fields={[
                  {
                    name: "description",
                    label: "Açıklama",
                    rich: true,
                    placeholder: DESC_PLACEHOLDER,
                  },
                ]}
                active={translationLang}
                onActiveChange={setTranslationLang}
              />
              {/* İyileştirme ve geri alma TABAN metni değiştiriyor. Başka bir dil
                                seçiliyken basılsaydı değişiklik ekranda olmayan bir alana yazılırdı,
                                o yüzden orada kapalılar.
                                Eskiden bu ikisi GİZLENİYORDU: dil sekmesi değişince buton ortadan
                                kayboluyor ve özellik gitmiş gibi duruyordu. Yerinde durup nedenini
                                söylemesi daha anlaşılır — satır yüksekliği de sabit kalıyor.
                                Başlıktaki "Diğer Dillere Çevir" bütün dilleri kapsadığı için her
                                sekmede açık. */}
              <Group gap="xs" justify="flex-end">
                {descriptionBeforeImprove !== null && (
                  <Tooltip
                    label={
                      onBaseLanguage
                        ? "İyileştirme öncesine dön"
                        : `Yalnızca ${base?.label ?? "taban dil"} sekmesinde`
                    }
                  >
                    <ActionIcon
                      aria-label="İyileştirmeyi geri al"
                      variant="subtle"
                      color="gray"
                      onClick={undoImprove}
                      disabled={!onBaseLanguage}
                    >
                      <IconArrowBackUp size={18} />
                    </ActionIcon>
                  </Tooltip>
                )}
                <Button
                  variant="subtle"
                  size="xs"
                  color="chocolate"
                  leftSection={<IconSparkles size={16} />}
                  loading={improving}
                  onClick={improveDescriptionField}
                  disabled={
                    !onBaseLanguage || !stripHtml(form.values.description)
                  }
                  title={
                    onBaseLanguage
                      ? undefined
                      : `Yalnızca ${base?.label ?? "taban dil"} açıklamasını iyileştirir`
                  }
                >
                  AI ile İyileştir
                </Button>
              </Group>
            </Stack>
          </FormSection>
        </Stack>

        <FormModalFooter>
          <Button variant="default" onClick={closeIfIdle} disabled={pushing}>
            Vazgeç
          </Button>
          <Button
            type="submit"
            loading={mutation.isPending}
            disabled={!canSubmit || pushing}
            leftSection={
              isEdit ? <IconDeviceFloppy size={16} /> : <IconPlus size={16} />
            }
          >
            {isEdit ? "Güncelle" : "Oluştur"}
          </Button>
        </FormModalFooter>
      </form>
    </Modal>
  );
}
